<template>
  <div ref="wrapper" class="flex font-sans">
    <ParagraphLink
      class="flex items-center truncate"
      :class="
        colorFromEntry({
          ...link,
          curated: Boolean(curated || linkChain?.curated),
        })
      "
      :legalText="linkedParagraph?.legal_text?.slug"
      :legalTextParagraph="linkedParagraph?.slug"
      :withPreview="!!(showPreview && !withinTeleport && linkedParagraph?.id)"
      :linkType="link.link_type"
      :oldVersionId="oldVersionId"
      :legalTextParagraphId="linkedParagraph?.id"
    >
      <slot name="icon">
        <i-heroicons-link
          v-if="icon"
          class="h-4 w-4 flex-shrink-0 fill-current"
        />
      </slot>
      <span
        :class="{ 'hover:text-lexmea-pop-lightest': showPreview }"
        class="ml-1 truncate"
        >{{ text }}<LawVersionIndicator :legalTextParagraph="linkedParagraph"
      /></span>
    </ParagraphLink>
    <p v-if="next">,</p>
  </div>
</template>

<script lang="ts" setup>
import type { ILink, ILinkChain } from "~/models/IUserEntry";

const props = withDefaults(
  defineProps<{
    icon?: boolean;
    linkChain?: ILinkChain;
    link: ILink;
    curated?: boolean; // cop out because of LinksOverview
    columnCollapsed?: boolean;
    showAdditionalText?: boolean;
  }>(),
  {
    showAdditionalText: true,
    linkChain: undefined,
  }
);

const { showPreview } = useParagraphPreview();

const linkedParagraph = computed(() =>
  incoming.value
    ? link.value.legal_text_paragraph
    : link.value.legal_text_paragraph_linked
);

const { oldVersionId } = useParagraphOldVersionId(linkedParagraph);
const { shortenedParagraph } = useShortenedParagraph(linkedParagraph);

const getSymbol = (paragraph?: string) =>
  paragraph?.match(/Art(\w+)?/) ? "Art." : paragraph?.includes("§") ? "§" : "";

const { link, linkChain } = toRefs(props);

const {
  query: { data: legalTextParagraph },
} = useCurrentParagraph();

const currentPos = computed(
  () => linkChain?.value?.links.indexOf(link.value) ?? -1
);

const next = computed(() =>
  currentPos.value === -1
    ? undefined
    : linkChain?.value?.links[currentPos.value + 1]
);

const previous = computed(() =>
  currentPos.value === -1
    ? undefined
    : linkChain?.value?.links[currentPos.value - 1]
);

const incoming = computed(
  () =>
    legalTextParagraph.value?.id === link.value.legal_text_paragraph_linked_id
);

const text = computed(() => {
  if (!showPreview.value) {
    return `${linkedParagraph.value.paragraph} ${linkedParagraph.value.legal_text?.name}`;
  }

  const currSymbol = getSymbol(linkedParagraph.value.paragraph);
  const nextSymbol = getSymbol(
    next.value?.legal_text_paragraph_linked.paragraph
  );

  const nextSameSymbol = nextSymbol === currSymbol;

  const previousSameSymbol =
    getSymbol(previous.value?.legal_text_paragraph_linked.paragraph) ===
    currSymbol;

  let text = "";

  if (!previousSameSymbol && props.columnCollapsed !== true) {
    text += currSymbol;

    if (nextSameSymbol && currSymbol === "§") {
      text += currSymbol;
    }
  }

  text += ` ${shortenedParagraph.value} `;

  if (props.showAdditionalText) {
    text += `${link.value.additional_text ?? ""} `;
  }

  if (
    next.value?.legal_text_paragraph_linked.legal_text_id !==
    linkedParagraph.value.legal_text_id
  ) {
    text += linkedParagraph.value.legal_text?.name;
  }

  return text;
});

const { colorFromEntry } = useColorFromEntry();

const wrapper = ref<HTMLElement>();
const { withinTeleport } = useWithinTeleport(wrapper);
</script>
